import React, { useEffect, useRef, useState } from 'react';
import { Instance, RuntimeMode, StreamStatus, useLazyGetStreamQuery, useLazyGetStreamFlowQuery, StreamInfo } from '../../../app/apiSlice';
import { ProgressSpinner } from 'primereact/progressspinner';
import { IStreamNameAndRunID } from '../editorSlice';
import { Console } from '../console/Console';
import { useTranslation } from 'react-i18next';

export interface InstanceStatuses {
    current: string,
    statuses: InstanceStatus[];
}

export interface InstanceStatus {
    status: string,
    data: Instance
}

export const LogsDatas = ({streamNameAndRunID, env}: { streamNameAndRunID: IStreamNameAndRunID, env: string }) => {
    const [getStream] = useLazyGetStreamQuery();
    const [getStreamFlow] = useLazyGetStreamFlowQuery();
    const [streamData, setStreamData] = useState<StreamInfo | null>(null);
    const [runIDChanged, setRunIDChanged] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const statusStopped = [StreamStatus.COMPLETED.toString(), StreamStatus.CANCELED.toString(), StreamStatus.TERMINATED.toString(), StreamStatus.FAILED.toString()];
    const [streamPods, setStreamPods] = useState<Map<string, Map<string, InstanceStatuses>>>(new Map());
    const timerStreamRef = useRef<NodeJS.Timer>();
    const alreadyTerminated = new Set<string>();
    const {t} = useTranslation();

    useEffect(() => {
        setRunIDChanged(true);
        const refresh = () => {
            if (streamNameAndRunID.name === "") {
                getStream({ topologyid: streamNameAndRunID.topologyId, env })
                    .unwrap()
                    .then(data => setStreamData(data));
            } else {
                getStreamFlow({ topologyid: streamNameAndRunID.topologyId, flowName: streamNameAndRunID.name, env })
                    .unwrap()
                    .then(data => setStreamData(data));
            }
        };

        refresh();
        timerStreamRef.current = setInterval(refresh, 3000);

        return () => {
            if (timerStreamRef.current) {
                clearInterval(timerStreamRef.current);
            }
        };
    }, [streamNameAndRunID.runID]);
    useEffect(() => {
        if (isLoading) {
            setTimeout(() => {
                setIsLoading(false);
                constructMapPodAndLogs([] as string[]);
            }, 2_000);
        }
    }, [runIDChanged])

    useEffect(() => {
        if (streamData) {
            if (statusStopped.includes(streamData.status) && timerStreamRef.current) {
                clearInterval(timerStreamRef.current);

                let updatedStatusMap: Map<string, Map<string, InstanceStatuses>> = new Map();
                streamPods.forEach((mapPodNameAndStatuses:  Map<string, InstanceStatuses>, streamName: string) => {
                    let updatedInstancesStatuses: Map<string, InstanceStatuses> = new Map();
                    mapPodNameAndStatuses.forEach((instanceStatuses: InstanceStatuses, podName: string) => {
                        updatedInstancesStatuses.set(podName, {...instanceStatuses, current: streamData.status})
                    })
                    updatedStatusMap.set(streamName, updatedInstancesStatuses);
                });
                setStreamPods(updatedStatusMap);
            } else if (streamData.pods?.find(p => p.instances?.length !== 0)) {
                const podDifferences = findPodDifferences(streamData.pods, streamPods);

                if (runIDChanged) {
                    setRunIDChanged(false);
                    setStreamPods(new Map());
                    setIsLoading(true);
                } else {
                    constructMapPodAndLogs(podDifferences);
                }
            }
        }
    }, [streamData]);

    const findPodDifferences = (
        podsArray: { name: string; instances: { podname: string; containername: string }[] }[],
        podsMap: Map<string, Map<string, any>>
      ) => {
        const missingInstances: string[] = [];
        podsMap.forEach((instanceMap, podName) => {
          const pod = podsArray.find(p => p.name === podName);
          if (!pod) {
            missingInstances.push(...instanceMap.keys());
            return;
          }
          const arrayInstances = pod.instances.map(i => {
            const id = i.podname.split(podName).pop()?.replace(/^-/, "") ?? i.podname;
            return `${id}/${i.containername}`;
          });
          instanceMap.forEach((_, instanceKey) => {
            if (!arrayInstances.includes(instanceKey)) {
              missingInstances.push(instanceKey);
            }
          });
        });
        return missingInstances;
      };

      const constructMapPodAndLogs = (podDifferences: string[]) => {
        if (!streamData) return;

        streamData.pods?.forEach(pod => {
          const displayName =
            streamData.runtime_type === RuntimeMode.ANALYTIC
              ? streamData.flowId
              : pod.name;

          const mapPodNameStatus = getOrCreateMapPodsByPodName(displayName)!;

          pod.instances?.forEach(instance => {
            insertPodStatus(mapPodNameStatus, displayName, instance);
          });

          // one pod is not in log updated, but still in the known pod, so it's terminated
          podDifferences.forEach(uniqueId => {
            const containerPart = uniqueId.split("/").pop() ?? "";
            const suffix = containerPart.split("-").pop() ?? "";
            if (pod.name === suffix && !alreadyTerminated.has(uniqueId)) {
              markTerminated(mapPodNameStatus, uniqueId);
              alreadyTerminated.add(uniqueId);
            }
          });
        });
      };

      const markTerminated = (
        mapPodNameStatus: Map<string, InstanceStatuses>,
        uniqueId: string
      ) => {
        if (!mapPodNameStatus.has(uniqueId)) {
          mapPodNameStatus.set(uniqueId, { current: '', statuses: [] });
        }

        const instanceStatuses = mapPodNameStatus.get(uniqueId)!;
        instanceStatuses.current = "terminated";

        const status = instanceStatuses.statuses.find(s => s.status === "terminated");
        if (!status) {
          instanceStatuses.statuses.push({ status: "terminated", data: { status: "terminated" } as Instance });
        }
        instanceStatuses.statuses.sort((a, b) => {
          const labelA = t("kube-status." + a.status.toLowerCase());
          const labelB = t("kube-status." + b.status.toLowerCase());
          return labelA.localeCompare(labelB);
        });
      };

    const getOrCreateMapPodsByPodName = (imageDockerName: string) => {
        if (!streamPods.has(imageDockerName)) {
            streamPods.set(imageDockerName, new Map<string, InstanceStatuses>());
        }
        return streamPods.get(imageDockerName);
    }

    const insertPodStatus = (mapPodNameStatus: Map<string, InstanceStatuses>, displayName: string, instance: Instance) => {
        let instanceName = instance.podname.split(displayName).pop() ?? instance.podname;

        if (instanceName.startsWith('-')) {
            instanceName = instanceName.slice(1); // or input.substring(1)
        }

        instanceName = instanceName + "/" + instance.containername;

        if (!mapPodNameStatus.has(instanceName)) {
            mapPodNameStatus.set(instanceName, {current: '', statuses: []});
        }

        const instanceStatuses = mapPodNameStatus.get(instanceName)!;
        instanceStatuses.current = instance.status;
        const status = instanceStatuses.statuses.find(instanceStatus => instanceStatus.status === instance.status);
        if (status) {
            status.data = instance;
        } else {
            instanceStatuses.statuses.push({status: instance.status, data: instance});
        }
        instanceStatuses.statuses.sort((a, b) => {
            const labelA = t("kube-status." + a.status.toLowerCase());
            const labelB = t("kube-status." + b.status.toLowerCase());

            return (labelA < labelB) ? -1 : (labelA > labelB) ? 1 : 0;
        });
    }

    return <>
        {/* Logs Container : Menu + Logs */}
        {!isLoading && streamData && (
            <Console streamPods={streamPods} flowStatus={streamData.status} />
        )}
        {isLoading && <ProgressSpinner/>}
    </>
}
